import React, { useState, useEffect, useRef } from 'react';
import { trackFunnelEvent } from '@/hooks/useFunnelTracking';
import { trackCartOpen, trackCartAbandonment, clearCartTimer } from '@/lib/ga4Advanced';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { useCartStore, type CardCustomization } from '@/stores/cartStore';
import { prefetchRoute } from '@/components/LocalizedRoutes';
import { VatNote } from '@/components/VatNote';
import { supabase } from '@/integrations/supabase/client';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { ShoppingBag, Trash2, Edit3, CreditCard, Loader2, Tag, X, Check } from 'lucide-react';
import { CartItemCustomization } from '@/components/cart/CartItemCustomization';
import { CartEditModal } from '@/components/cart/CartEditModal';
import { Input } from '@/components/ui/input';

interface EditingItem {
  localId: string;
  productSlug: string;
  productName: string;
  customization: CardCustomization;
}

export const CartDrawer: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { 
    items, 
    isOpen, 
    isLoading, 
    lastAddedItemId,
    appliedDiscount,
    closeCart, 
    removeItem, 
    updateCustomization,
    cartTotal,
    cartSubtotal,
    discountAmount,
    applyDiscount,
    removeDiscount,
    clearLastAddedItemId,
  } = useCartStore();
  
  const [editingItem, setEditingItem] = useState<EditingItem | null>(null);
  const [animatingItemId, setAnimatingItemId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');
  const total = cartTotal();
  const subtotal = cartSubtotal();
  const currencyCode = items[0]?.price.currencyCode || 'EUR';

  // Track cart_viewed when drawer opens + start cart timer + prefetch checkout
  useEffect(() => {
    if (isOpen && items.length > 0) {
      const total = items.reduce((sum, item) => sum + parseFloat(item.price.amount) * item.quantity, 0);
      trackFunnelEvent('cart_viewed', { cart_value: total, items_count: items.length, currency: 'EUR' });
      trackCartOpen();
      // Prefetch checkout chunk so it's ready when user clicks "Checkout"
      prefetchRoute('/checkout');
    }
    // Track abandonment when cart closes without checkout
    if (!isOpen) {
      trackCartAbandonment();
    }
  }, [isOpen]);

  // Animate newly added item with delay
  useEffect(() => {
    if (lastAddedItemId && isOpen) {
      // Scroll to the new item first
      setTimeout(() => {
        const itemElement = document.getElementById(`cart-item-${lastAddedItemId}`);
        if (itemElement && scrollRef.current) {
          itemElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 100);
      
      // Start animation after 0.5s delay
      const startTimer = setTimeout(() => {
        setAnimatingItemId(lastAddedItemId);
      }, 500);
      
      // Clear animation after it completes (0.5s delay + 1.2s animation)
      const endTimer = setTimeout(() => {
        setAnimatingItemId(null);
        clearLastAddedItemId();
      }, 1700);
      
      return () => {
        clearTimeout(startTimer);
        clearTimeout(endTimer);
      };
    }
  }, [lastAddedItemId, isOpen, clearLastAddedItemId]);

  const handleEditClick = (item: typeof items[0]) => {
    if (item.customization) {
      setEditingItem({
        localId: item.localId,
        productSlug: item.productSlug,
        productName: item.productName,
        customization: item.customization,
      });
    }
  };

  const handleSaveCustomization = (customization: CardCustomization) => {
    if (editingItem) {
      // Preserve existing metadata (templateSlug, templateName, cardPreviewDataUrl)
      // that the edit modal doesn't manage
      const merged: CardCustomization = {
        ...editingItem.customization,
        ...customization,
      };
      updateCustomization(editingItem.localId, merged);
    }
  };

  const handleCheckout = () => {
    const total = items.reduce((sum, item) => sum + parseFloat(item.price.amount) * item.quantity, 0);
    trackFunnelEvent('checkout_started', {
      cart_value: total,
      items_count: items.length,
      currency: 'EUR',
    });
    closeCart();
    navigate('/checkout');
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    setCouponError('');
    try {
      const { data, error } = await supabase.functions.invoke('validate-discount-code', {
        body: { code: couponCode.trim(), cart_total: subtotal },
      });
      if (error) throw error;
      if (data.valid) {
        applyDiscount({
          code: data.code,
          type: data.discount_type,
          value: data.discount_value,
          amount: data.discount_amount,
        });
        setCouponCode('');
        setCouponError('');
      } else {
        setCouponError(data.message || 'Ungültiger Code');
      }
    } catch {
      setCouponError('Fehler bei der Validierung');
    } finally {
      setCouponLoading(false);
    }
  };

  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);
  };

  return (
    <>
      <Sheet open={isOpen} onOpenChange={closeCart}>
        <SheetContent className="w-full sm:max-w-lg flex flex-col h-full">
          <SheetHeader className="pb-4 border-b border-border flex-shrink-0">
            <SheetTitle className="flex items-center gap-2">
              <ShoppingBag className="h-5 w-5" />
              {t('cart.title')}
              {isLoading && (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              )}
            </SheetTitle>
          </SheetHeader>

          {items.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center py-12">
              <div className="h-20 w-20 rounded-full bg-secondary flex items-center justify-center mb-4">
                <ShoppingBag className="h-10 w-10 text-muted-foreground" />
              </div>
              <p className="text-lg font-medium text-foreground mb-2">{t('cart.empty')}</p>
              <p className="text-muted-foreground mb-6">{t('cart.emptySubtext')}</p>
              <Button onClick={closeCart} variant="outline">
                {t('cart.continueShopping')}
              </Button>
            </div>
          ) : (
            <>
              {/* Cart Items */}
              <div ref={scrollRef} className="flex-1 overflow-auto py-4 space-y-4 min-h-0">
                {items.map((item) => (
                  <div 
                    key={item.localId} 
                    id={`cart-item-${item.localId}`}
                    className="flex gap-3 p-3 rounded-lg bg-secondary/50"
                    style={animatingItemId === item.localId ? {
                      animation: 'cart-item-highlight 1.2s ease-in-out'
                    } : undefined}
                  >
                    {/* Product Image - Always show stable product image (not user-generated preview) */}
                    <div 
                      className="h-12 rounded-md bg-muted flex items-center justify-center overflow-hidden shrink-0 select-none"
                      style={{
                        width: `${12 * 1.6 * 4}px`, // h-12 = 48px, so width = 48 * 1.6 = ~77px
                        aspectRatio: '1.6 / 1',
                        backgroundImage: item.productImage
                          ? `url(${item.productImage})`
                          : undefined,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                      }}
                      draggable={false}
                      onDragStart={(e) => e.preventDefault()}
                      onContextMenu={(e) => e.preventDefault()}
                    />

                    {/* Product Details */}
                    <div className="flex-1 min-w-0 space-y-1">
                      <h4 className="font-medium text-foreground text-sm truncate">
                        {item.customization?.cartDisplayName || item.productName}
                      </h4>
                      <p className="text-sm text-primary font-semibold">
                        {formatPrice(parseFloat(item.price.amount))}
                      </p>
                      
                      {/* Variant info */}
                      {item.variantTitle && item.variantTitle !== 'Default Title' && (
                        <p className="text-xs text-muted-foreground">
                          {item.variantTitle}
                        </p>
                      )}
                      
                      {/* Customization Summary */}
                      {item.customization && (
                        <CartItemCustomization customization={item.customization} />
                      )}
                    </div>

                    {/* Actions & Price */}
                    <div className="flex flex-col items-end justify-between gap-1">
                      <div className="flex gap-1">
                        {item.customization && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground hover:bg-primary hover:text-white"
                            onClick={() => handleEditClick(item)}
                            disabled={isLoading}
                          >
                            <Edit3 className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:bg-primary hover:text-white"
                          onClick={() => removeItem(item.localId)}
                          disabled={isLoading}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="font-semibold text-foreground">
                        {formatPrice(parseFloat(item.price.amount))}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Cart Footer */}
              <div className="border-t border-border pt-4 space-y-3 flex-shrink-0 bg-background">
                {/* Coupon Input */}
                {!appliedDiscount ? (
                  <div className="space-y-1">
                    <div className="flex gap-2">
                      <Input
                        value={couponCode}
                        onChange={e => { setCouponCode(e.target.value.toUpperCase()); setCouponError(''); }}
                        placeholder={t('cart.couponPlaceholder')}
                        className="font-mono text-sm h-9"
                        onKeyDown={e => e.key === 'Enter' && handleApplyCoupon()}
                        onBlur={() => { if (couponCode.trim()) handleApplyCoupon(); }}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleApplyCoupon}
                        disabled={couponLoading || !couponCode.trim()}
                        className="h-9 px-3"
                      >
                        {couponLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Tag className="h-4 w-4" />}
                      </Button>
                    </div>
                    {couponError && <p className="text-xs text-destructive">{couponError}</p>}
                  </div>
                ) : (
                  <div className="flex items-center justify-between p-2 rounded-md bg-primary/10 border border-primary/20">
                    <div className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-primary" />
                      <span className="text-sm font-mono font-semibold">{appliedDiscount.code}</span>
                      <span className="text-xs text-muted-foreground">
                        ({appliedDiscount.type === 'percentage' ? `${appliedDiscount.value}%` : `€${appliedDiscount.value.toFixed(2)}`})
                      </span>
                    </div>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={removeDiscount}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}

                {/* Subtotal */}
                {appliedDiscount && (
                  <>
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <span>Zwischensumme</span>
                      <span>{formatPrice(subtotal)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm text-primary">
                      <span>Rabatt</span>
                      <span>-{formatPrice(discountAmount())}</span>
                    </div>
                  </>
                )}

                <div className="flex items-center justify-between text-lg font-semibold">
                  <span>{t('cart.subtotal')}</span>
                  <span>{formatPrice(total)}</span>
                </div>
                <VatNote className="text-right -mt-2" />

                <Button 
                  onClick={handleCheckout} 
                  className="w-full" 
                  size="lg"
                  disabled={items.length === 0 || isLoading}
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CreditCard className="w-4 h-4 mr-2" />
                  )}
                  {t('cart.checkout')}
                </Button>

                <button
                  onClick={closeCart}
                  className="w-full py-3 text-sm font-medium text-muted-foreground hover:text-primary transition-colors duration-200"
                >
                  {t('cart.continueShopping')}
                </button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Edit Modal */}
      {editingItem && (
        <CartEditModal
          isOpen={!!editingItem}
          onClose={() => setEditingItem(null)}
          productId={editingItem.productSlug}
          productName={editingItem.productName}
          customization={editingItem.customization}
          onSave={handleSaveCustomization}
        />
      )}
    </>
  );
};
